import {
  ApolloClient, createHttpLink, from, InMemoryCache, split,
} from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import Cookies from 'js-cookie';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getMainDefinition } from '@apollo/client/utilities';
// eslint-disable-next-line import/extensions
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import store from '~/store';
import { calendarTimelineToggleHeaders } from '~/helpers/calendarTimelineToggleHeader';
import lazyPusherLink from './lazyPusherLink';
import typePolicies from './apolloCachePolicies';

// Batch HTTP connection to the API
const batchHttpLink = new BatchHttpLink({
  uri: '/graphql',
  batchMax: 5, // No more than 5 operations per batch
  batchInterval: 20, // Wait no more than 20ms after first batched operation
});

// Classic HTTP connection to the API
const classicHttpLink = createHttpLink();

// Link that will redirect to the Batch of the Classic link
const redirectLink = split(
  (operation) => operation.getContext().disableBatch,
  classicHttpLink,
  batchHttpLink,
);

// Using the split function to send data to each link
const subscriptionVersusOtherLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition'
      && definition.operation === 'subscription'
    );
  },
  lazyPusherLink,
  redirectLink,
);

// Ensures there is an authentication token
export const ensureAuthenticationToken = async function () {
  let token;
  try {
    let accessToken = await fetchAuthSession({ forceRefresh: false })
      .then((session) => session?.tokens?.accessToken);

    if (!accessToken) {
      Cookies.remove('token');
      return;
    }

    const now = (new Date().getTime() / 1000);
    const remainingTime = (accessToken?.payload?.exp ?? now) - now;
    if (remainingTime < 10) {
      // Force authentication when the token is about to expire
      accessToken = await fetchAuthSession({ forceRefresh: true })
        .then((session) => session?.tokens?.accessToken);
    }

    token = accessToken.toString();
  } catch (e) {
    console.error(e);
  } finally {
    if (token) {
      Cookies.set('token', token, { expires: null });
    }
  }
};

// ApolloLink to ensure there is an authentication token beforehand
const authLink = setContext(async (request, context) => {
  const isAnonymous = context?.anonymous ?? false;
  if (!isAnonymous) {
    await ensureAuthenticationToken();
  }

  let headers = context.headers || {};
  headers = {
    ...headers,
    ...{
      'x-kp-web-version': window?.keyprod?.version,
    },
    // Calendars-to-Timeline toggle (E-0101 / S-0010): forwards
    // X-Kp-Calendar-Timeline to the kpweb backend on every GraphQL call when the
    // console flag is active. No-op (empty) otherwise. See calendarTimelineToggleHeader.
    ...calendarTimelineToggleHeaders(),
  };

  return {
    headers,
  };
});

const errorLink = onError(({
  graphQLErrors,
  networkError,
  operation,
  forward,
}) => {
  // Handles authentication error
  if (graphQLErrors && graphQLErrors.some(({ message }) => message === 'Unauthenticated.')) {
    // Pas sur la page de connexion : là, un « Unauthenticated » n'est pas une session qui
    // expire mais un refus d'entrer, que la page affiche elle-même. La modale annoncerait
    // une déconnexion à quelqu'un qui n'est jamais entré, et masquerait le vrai message.
    if (!window.location.pathname.startsWith('/login')) {
      store.commit('auth/commitShowDisconnectModal', true);
    }

    return;
  }

  if (networkError?.statusCode === 409) {
    window.location.reload();
    return;
  }

  // Handle batch operation errors by retrying with batch disabled
  const networkMsg = networkError?.message || '';
  const gqlMsgs = Array.isArray(graphQLErrors) ? graphQLErrors.map((e) => e.message)
    .join(' | ') : '';
  const combined = `${networkMsg} ${gqlMsgs}`;

  if (combined.includes('server returned results with length')) {
    operation.setContext({ disableBatch: true });
    /* eslint-disable-next-line */
    return forward(operation);
  }
});

// Cache implementation
const cache = new InMemoryCache({ typePolicies });

// Client dedicated to file upload
export const uploadFileClient = new ApolloClient({
  link: createUploadLink({
    uri: '/graphql',
    credentials: 'include',
  }),
  cache,
});

// Create the Apollo Client
export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, subscriptionVersusOtherLink]),
  cache,
  queryDeduplication: true,
  defaultOptions: {
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
    watchQuery: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  },
});
